import { Components } from '../../../components';
import JuiSelect = Components.JuiSelect;
import { SelectOption } from './jui-select.model';
import { isDefined, isObject } from './jui-select-utils';

export class JuiSelectItems {
  constructor(private _juiSelect: JuiSelect) {}

  private _items: SelectOption[] = [];
  private _filteredItems: SelectOption[] = [];
  private _selectedItems: SelectOption[] = [];
  private _selectedValue: any[] = [];
  private _lastSearchTerm: string = null;
  private _currentFocusIndex: number = null;

  get items(): SelectOption[] {
    return this._items;
  }

  get filteredItems(): SelectOption[] {
    return this._filteredItems;
  }

  get selectedItems(): SelectOption[] {
    return this._selectedItems;
  }

  get selectedValue(): any[] {
    return this._selectedValue;
  }

  get activeItem(): SelectOption {
    return this._items.find(x => x.focused);
  }

  unsetActiveItem(): void {
    this._items = this._items.map(x => ({ ...x, focused: false }));
  }

  setActiveItem(): void {
    let items = this.getOrderedOptions().map((x, i) => ({
      ...x,
      focused: i === this._currentFocusIndex
    }));

    if (!this._juiSelect.multiple) {
      items = this.filteredItems.map((x, i) => ({ ...x, focused: i === this._currentFocusIndex }));
    }

    // todo optimise this
    this._items = this._items.map(x => ({ ...x, focused: items.find(itm => itm.value === x.value)?.focused }));
  }

  setNextActiveItemAfterUnSelect(): void {
    this.setActiveItem();
    this.filterItems();
  }

  selectAll(): void {
    this.clearSelectedItems();
    this._items.forEach(item => {
      item.selected = true;
      this._selectedItems.push(item);
    });
    this.mapSelectedItemValues();
  }

  select(item: SelectOption): void {
    if (item.selected) {
      return;
    }

    // find the item in the _item list as also update that
    const itemToSelect = this.findItemByValue(item.value);

    if (!this._juiSelect.multiple) {
      this.clearSelectedItems();
      item.selected = true;
      item.focused = false;
      itemToSelect.selected = true;
      this._selectedItems = [item];
    } else {
      item.selected = true;
      item.focused = false;
      itemToSelect.selected = true;
      this._selectedItems.push(item);
    }

    if (this._juiSelect.typeahead && this._juiSelect.multiple) {
      this.filterItems(this._lastSearchTerm);
    }

    this.mapSelectedItemValues();
    if (this._juiSelect.typeahead && this.filteredItems.length <= this._currentFocusIndex + 1) {
      this._currentFocusIndex--;
    }
    this.setFocus(this._currentFocusIndex);
  }

  mapSelectedItemValues(): void {
    // resolves and sets the selected value list
    if (this._selectedItems) {
      this._selectedValue = this._selectedItems.map(x => {
        let val = x.value;
        if (this._juiSelect.bindvalue) {
          val = this.findNested(x.value, this._juiSelect.bindvalue);
        }
        return val;
      });
    }

    if (this._juiSelect.typeahead) {
      // we want to map the current selected items back to the items
      this._items = this.items.map(itm => ({
        ...itm,
        selected: this.selectedItems.find(si => itm.label === si.label)?.selected
      }));
    }
  }

  unselect(item: SelectOption): void {
    if (!this._juiSelect.multiple) {
      return;
    }
    item.selected = false;
    // find the item in the _item list as also update that
    const itemToSelect = this.findItemByValue(item.value);
    if (itemToSelect) {
      itemToSelect.selected = false;
    }

    if (this._juiSelect.multiple) {
      this.filterItems(this._lastSearchTerm);
    }

    this.mapSelectedItemValues();
    this.setNextActiveItemAfterUnSelect();
  }

  getCurrentFocusIndex(): number {
    return this._currentFocusIndex;
  }

  clearSelectedItems(): void {
    this._items.forEach(item => (item.selected = false));
    this._filteredItems.forEach(item => (item.selected = false));
    this._selectedItems = [];
  }

  setItems(items): void {
    // Preserve the selected state when items are updated
    const previouslySelectedValues = this._selectedItems.map(item => {
      if (this._juiSelect.bindvalue && !this._juiSelect.typeahead) {
        return this.findNested(item.value, this._juiSelect.bindvalue);
      }
      return item.value;
    });

    // Map new items and restore selected state
    this._items = items.map(item => {
      const mappedItem = this.mapItem(item);

      // Check if this item was previously selected
      const itemValue =
        this._juiSelect.bindvalue && !this._juiSelect.typeahead
          ? this.findNested(mappedItem.value, this._juiSelect.bindvalue)
          : mappedItem.value;

      const wasSelected = previouslySelectedValues.some(selectedValue => {
        if (typeof selectedValue === 'object' && typeof itemValue === 'object') {
          return JSON.stringify(selectedValue) === JSON.stringify(itemValue);
        }
        return selectedValue === itemValue;
      });

      if (wasSelected) {
        mappedItem.selected = true;
      }

      return mappedItem;
    });

    this._filteredItems = [...this._items];

    // Update selectedItems to reference the new items while preserving selection
    this._selectedItems = this._items.filter(item => item.selected);

    // Remap selected values to ensure consistency
    this.mapSelectedItemValues();
  }

  private _getNextItemIndex(steps: number) {
    if (steps > 0) {
      return this._currentFocusIndex >= this._filteredItems.length - 1 ? 0 : this._currentFocusIndex + 1;
    }
    return this._currentFocusIndex <= 0 ? this._filteredItems.length - 1 : this._currentFocusIndex - 1;
  }

  private stepToItem(steps: number): void {
    if (this._filteredItems.length === 1) {
      this._currentFocusIndex = 0;
      return;
    }
    this._currentFocusIndex = this._getNextItemIndex(steps);

    const itemList = this.getOrderedOptions();

    const activeItem = itemList.at(this._currentFocusIndex) as SelectOption;
    if (itemList.every(x => x.selected) || !itemList.length) {
      return;
    }

    if (!!(this._juiSelect.hideselected || this._juiSelect.typeahead) && (activeItem.disabled || activeItem.selected)) {
      this.stepToItem(steps);
    } else if (activeItem.disabled) {
      this.stepToItem(steps);
    }
  }

  setFocusFromItem(focusedItem: SelectOption): void {
    const itemList = this.getOrderedOptions();
    const newIndex = itemList.findIndex(x => focusedItem.value === x.value);
    if (newIndex >= 0) {
      this.setFocus(newIndex);
    }
  }

  setFocus(index: number): void {
    if (index === null || index === undefined) {
      return;
    }

    if (this._filteredItems.length) {
      const itemList = this.getOrderedOptions();

      if (itemList.length === 1) {
        index = 0;
      }
      const itemToFocusValue = itemList[index].value;
      this._items.forEach(item => (item.focused = item.value === itemToFocusValue));
      this._filteredItems.forEach(item => (item.focused = item.value === itemToFocusValue));
      this._filteredItems = this.filteredItems.map(x => x);
      this._currentFocusIndex = index;
    }
  }

  private getOrderedOptions(): SelectOption[] {
    return [...this._filteredItems.filter(x => x.selected), ...this._filteredItems.filter(x => !x.selected)];
  }

  setNextFocused(term?: string): void {
    this.stepToItem(1);
    this.setActiveItem();
    this.filterItems(term);
  }

  setPrevFocused(term?: string): void {
    this.stepToItem(-1);
    this.setActiveItem();
    this.filterItems(term);
  }

  findItemByValue(value: any): SelectOption {
    let findFunc: (item: SelectOption) => boolean;
    if (this._juiSelect.bindvalue && !this._juiSelect.typeahead) {
      // find by boundValue
      findFunc = item =>
        this.findNested(item.value, this._juiSelect.bindvalue) === this.findNested(value, this._juiSelect.bindvalue);
    } else if (isObject(value)) {
      // find by object
      findFunc = item => JSON.stringify(item.value) === JSON.stringify(value);
    } else {
      // find by simple value
      findFunc = item => item.value === value;
    }
    return this._items.find(findFunc);
  }

  private replaceSearchTerm(item, term) {
    const highlightedLabel = item.label.replace(
      new RegExp(term, 'gi'),
      `<span class="jui-select__highlighted">$&</span>`
    );
    return { ...item, highlightedLabel };
  }

  private highlightSearchTerm(term: string): void {
    this._filteredItems = this._filteredItems.map(item => this.replaceSearchTerm(item, term));
  }

  filterItems(term?): void {
    this._lastSearchTerm = term;
    if (term && term.length) {
      this._filteredItems = this._items.filter(x => x.label.toLowerCase().indexOf(term.toLowerCase()) !== -1);
      this._filteredItems = this.getOrderedOptions().map((x, i) => ({ ...x, focused: i === this._currentFocusIndex }));
      if (this._juiSelect.highlight) {
        // add highlighted label prop on item
        this.highlightSearchTerm(term);
      }
    } else {
      this.resetFilteredItems();
    }
    if (
      this._juiSelect.typeahead &&
      this._juiSelect.multiple &&
      (this._selectedItems.length || this._juiSelect.value)
    ) {
      if (this._currentFocusIndex === null) {
        this._currentFocusIndex = 0;
      }
      this._selectedItems = this._selectedItems.filter(x => x.selected);
      const selectedItemValues = this._selectedItems.map(si => JSON.stringify(si.value));
      // remove selected value from the filtered list aad refocus
      this._filteredItems = this._filteredItems
        .filter(x => !selectedItemValues.includes(JSON.stringify(x.value)))
        .filter(x => !x.selected)
        .map((x, i) => ({ ...x, focused: i === this._currentFocusIndex }));
    }
  }

  resetFilteredItems(): void {
    this._filteredItems = this._items;
    if (!this._juiSelect.typeahead) {
      this._selectedItems = this._items.filter(x => x.selected);
    }
  }

  parseAndAddItem(item: any): SelectOption {
    const newItem = this.mapItem(item);
    this._items.push(newItem);
    this._filteredItems.push(newItem);
    return newItem;
  }

  mapItem(item): SelectOption {
    const label = isDefined(item.label) ? item.label : this.findNested(item, this._juiSelect.bindlabel);
    const value = isDefined(item.value) ? item.value : item;
    const selected = isDefined(item.selected) ? item.selected : false;
    const disabled = isDefined(item.disabled) ? item.disabled : false;
    const focused = isDefined(item.focused) ? item.focused : false;
    return {
      label,
      value,
      selected,
      disabled,
      focused
    };
  }

  findNested(item, propKey: string): any {
    if (!isObject(item)) {
      return item;
    }
    if (propKey.indexOf('.') === -1) {
      return item[propKey];
    } else {
      // resolve nested
      const keys = propKey.split('.');
      let val = item;

      val = keys.reduce((previousValue: string, currentValue: string) => {
        return previousValue[currentValue];
      }, item);
      return val;
    }
  }
}
